import { UIHeading3 } from '@cian/ui-kit/typography';
import * as React from 'react';

import { trackSuggestClick } from './tracking';
import { CellGroup } from '../../components/CellGroup';
import { SuggestItem } from '../../components/SuggestItem/SuggestItem';
import { ISuggestion } from '../../types/suggestions';
import { ETelemetryEvents } from '../../types/telemetry';
import { useApplicationContext } from '../ApplicationContext';
import { Cell } from 'shared/components/Cell';
import { useSearchStringUsed } from 'shared/hooks/useSearchStringUsed';

interface ISearchBlockSuggestsContainerProps {
  query: string;
  suggestions: ISuggestion[];
  withNotFoundText: boolean;
  setLoading?(isLoading: boolean): void;
}

export const SearchBlockSuggestsContainer: React.FC<ISearchBlockSuggestsContainerProps> = ({
  query,
  suggestions,
  withNotFoundText,
  setLoading,
}) => {
  const { telemetry } = useApplicationContext();
  const shouldRenderNotFoundText = withNotFoundText && query && suggestions.length === 0;
  const { setSearchStringUsed } = useSearchStringUsed();

  React.useEffect(() => {
    if (shouldRenderNotFoundText) {
      telemetry.increment(ETelemetryEvents.NotFoundSuggestionsSuccess);
    }
  }, [shouldRenderNotFoundText, telemetry]);

  const handleSuggestClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, suggest: ISuggestion, index: number) => {
      if (setLoading) {
        setLoading(true);
      }
      e.preventDefault();

      const url = new URL(e.currentTarget.href);
      url.searchParams.set('source', 'search_string');
      const href = url.toString();

      if (suggest.type !== 'last_search') {
        localStorage.setItem(
          'searchQuery',
          JSON.stringify({
            ...suggest,
            type: 'last_search',
            icon: undefined,
            isUserSuggest: true,
          }),
        );
      }

      trackSuggestClick({
        name: e.currentTarget.text,
        query,
        suggestPos: `${index + 1}`,
        totalSuggest: `${suggestions.length}`,
        href,
        suggestType: suggest.type,
        isDuplicateSuggest: suggest.isUserSuggest,
      });

      setTimeout(() => {
        window.location.href = href;
      }, 300);

      setSearchStringUsed();
    },
    [query, setLoading, setSearchStringUsed, suggestions.length],
  );

  return (
    <div data-testid="SearchBlockSuggestsContainer">
      {shouldRenderNotFoundText && (
        <UIHeading3 color="text-secondary-default">
          Ничего не нашли, попробуйте
          <br />
          по-другому
        </UIHeading3>
      )}
      <CellGroup>
        {suggestions.map((suggest, index) => (
          <Cell
            key={`suggest_${index}_${suggest.url}`}
            href={suggest.url}
            onClick={e => handleSuggestClick(e, suggest, index)}
          >
            <SuggestItem suggest={suggest} />
          </Cell>
        ))}
      </CellGroup>
    </div>
  );
};
